if (!customElements.get('complete-the-look')) {
  customElements.define('complete-the-look', class CompleteTheLook extends HTMLElement {
    connectedCallback() {
      this.checkboxes = [...this.querySelectorAll('[data-complete-look-checkbox]')];
      this.button = this.querySelector('[data-complete-look-add]');
      this.summary = this.querySelector('[data-complete-look-summary]');
      this.status = this.querySelector('[data-complete-look-status]');
      this.bundlePricing = this.querySelector('[data-complete-look-bundle-pricing]');
      this.comparePrice = this.querySelector('[data-complete-look-compare-price]');
      this.bundlePrice = this.querySelector('[data-complete-look-bundle-price]');
      this.savings = this.querySelector('[data-complete-look-savings]');
      this.bundleLink = this.querySelector('[data-complete-look-bundle-link]');
      this.currency = this.dataset.currencyCode || 'USD';
      this.bundles = this.parseBundles();

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-complete-look-checkbox]')) this.enforceLimit(event.target);
        if (event.target.matches('[data-complete-look-variant]')) this.updateVariantPrice(event.target);
        this.update();
      });
      this.button?.addEventListener('click', () => this.addSet());
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-complete-look-add-single]');
        if (button) this.addSingle(button);
      });
      this.update();
    }

    selectedCards() {
      return this.checkboxes
        .filter((checkbox) => checkbox.checked)
        .map((checkbox) => checkbox.closest('[data-complete-look-card]'));
    }

    enforceLimit(changedCheckbox) {
      if (this.selectedCards().length > 2) changedCheckbox.checked = false;
    }

    variantData(card) {
      const control = card.querySelector('[data-complete-look-variant]');
      const option = control.matches('select') ? control.selectedOptions[0] : control;
      return { id: String(control.value), price: Number(option.dataset.price) };
    }

    updateVariantPrice(control) {
      const card = control.closest('[data-complete-look-card]');
      const price = card.querySelector('[data-complete-look-price]');
      if (price) price.textContent = this.money(this.variantData(card).price);
      const button = card.querySelector('[data-complete-look-add-single]');
      if (button) button.textContent = `Add to cart — ${this.money(this.variantData(card).price)}`;
    }

    mainVariantId() {
      const completeLookControl = this.querySelector('[data-complete-look-single-card] [data-complete-look-variant]');
      return String(
        completeLookControl?.value
        || document.querySelector('product-info product-form input[name="id"]:not([disabled])')?.value
        || this.dataset.mainVariantId
      );
    }

    mainPrice() {
      const productInfo = document.querySelector('product-info');
      const selectedId = this.mainVariantId();
      const variantData = productInfo?.querySelector('script[type="application/json"]');
      if (variantData) {
        try {
          const variants = JSON.parse(variantData.textContent);
          const variant = variants.find((item) => String(item.id) === selectedId);
          if (variant) return variant.price;
        } catch (error) {
          // The Liquid fallback remains accurate for this single-variant pilot product.
        }
      }
      return Number(this.dataset.mainPrice) || 0;
    }

    parseBundles() {
      const manifest = this.querySelector('[data-complete-look-bundles]');
      if (!manifest) return [];
      try {
        const bundles = JSON.parse(manifest.textContent);
        return Array.isArray(bundles)
          ? bundles.filter((bundle) => bundle.variantId && bundle.available !== false && bundle.components?.length >= 2)
          : [];
      } catch (error) {
        return [];
      }
    }

    bundleForSelection(selectedCards) {
      const selectedVariantIds = [
        this.mainVariantId(),
        ...selectedCards.map((card) => this.variantData(card).id),
      ].sort().join('|');

      return this.bundles.find((bundle) => bundle.components.map(String).sort().join('|') === selectedVariantIds);
    }

    update() {
      const selected = this.selectedCards();
      const total = this.mainPrice() + selected.reduce((sum, card) => sum + this.variantData(card).price, 0);
      const bundle = this.bundleForSelection(selected);
      this.summary.textContent = selected.length
        ? bundle ? `${selected.length + 1} items · Official bundle price` : `${selected.length + 1} items · ${this.money(total)}`
        : 'Select a coordinating piece';
      if (this.bundlePricing) {
        this.bundlePricing.hidden = !bundle;
        if (bundle) {
          this.comparePrice.textContent = this.money(bundle.compareAtPrice);
          this.bundlePrice.textContent = this.money(bundle.price);
          this.savings.textContent = `Save ${this.money(bundle.compareAtPrice - bundle.price)}`;
        }
      }
      if (this.bundleLink) {
        this.bundleLink.hidden = !bundle;
        if (bundle) {
          this.bundleLink.href = bundle.url;
          this.bundleLink.textContent = `Bundle: ${bundle.title} — ${bundle.variantTitle}`;
        } else {
          this.bundleLink.removeAttribute('href');
          this.bundleLink.textContent = '';
        }
      }
      this.button.disabled = selected.length === 0;
      this.button.textContent = selected.length
        ? bundle
          ? `Add Bundle to Cart — ${this.money(bundle.price)} · Save ${this.money(bundle.compareAtPrice - bundle.price)}`
          : `Add selected set — ${this.money(total)}`
        : 'Add selected set';
      this.checkboxes.forEach((checkbox) => {
        checkbox.disabled = !checkbox.checked && selected.length >= 2;
      });
    }

    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.currency,
      }).format(cents / 100);
    }

    async addSet() {
      const selected = this.selectedCards();
      if (!selected.length || this.button.disabled) return;

      this.button.disabled = true;
      const bundle = this.bundleForSelection(selected);
      this.status.textContent = bundle ? 'Adding your bundle…' : 'Adding your set…';
      const quantity = Number(document.querySelector('product-info input[name="quantity"]')?.value) || 1;
      const items = bundle
        ? [{ id: bundle.variantId, quantity }]
        : [
            { id: this.mainVariantId(), quantity },
            ...selected.map((card) => ({ id: this.variantData(card).id, quantity: 1 })),
          ];

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) throw new Error('The selected set could not be added.');
        window.location.assign(`${window.Shopify.routes.root}cart`);
      } catch (error) {
        this.status.textContent = `${error.message} Please try again.`;
        this.button.disabled = false;
      }
    }

    async addSingle(button) {
      if (button.disabled) return;

      const card = button.closest('[data-complete-look-card], [data-complete-look-single-card]');
      const item = this.variantData(card);
      const originalText = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding…';
      this.status.textContent = '';

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: item.id, quantity: 1 }] }),
        });
        if (!response.ok) throw new Error('This item could not be added.');
        window.location.assign(`${window.Shopify.routes.root}cart`);
      } catch (error) {
        this.status.textContent = `${error.message} Please try again.`;
        button.disabled = false;
        button.textContent = originalText;
      }
    }
  });
}

